import React, {
  Fragment,
  useState,
  useEffect,
  useCallback,
  useMemo
} from 'react'
import './LeaderboardTable.scss'
import { Icon, Input, Button, Avatar, Spin, Table, Modal, Tag, Descriptions } from 'antd'
import history from '../../../../history'
import api from '../../../../services/api'
import moment from 'moment'
import { ImagePreview } from '../../../../components'
import { useTranslation } from 'react-i18next'

const ExpandedQuestRow = ({ groupId }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setLoading(true)
        const response = await api.quest.getGroupProgress({ groupId })
        setData(response.data)
      } catch (e) {
        console.error(e)
      } finally {
        setLoading(false)
      }
    }
    fetchDetails()
  }, [groupId])

  if (loading) return <div style={{ padding: 16 }}><Spin size="small" /> Loading details...</div>
  if (!data || !data.stops || data.stops.length === 0) return <div style={{ padding: 16 }}>No details available</div>

  return (
    <div style={{ padding: '16px', background: '#fafafa', borderRadius: '8px', margin: '0 16px' }}>
      <h4 style={{ marginBottom: 16, color: '#1890ff' }}>{data.groupName} - Progress Details</h4>
      {data.stops.map(stop => (
        <div key={stop.number} style={{ marginBottom: 16, borderLeft: '3px solid #1890ff', paddingLeft: 12 }}>
          <h5 style={{ marginBottom: 8, fontSize: '14px' }}>Stop {stop.number} <Tag color="blue" style={{ marginLeft: 8 }}>{stop.score} Pts</Tag></h5>
          <Descriptions size="small" column={1} bordered style={{ background: '#fff' }}>
            {stop.mc && (
              <Descriptions.Item label="Multiple Choice">
                <span style={{ marginRight: 16 }}>Selected: {stop.mc.selectedIndex}</span>
                <Tag color={stop.mc.correct ? 'green' : 'red'}>
                  {stop.mc.correct ? 'Correct' : 'Incorrect'}
                </Tag>
              </Descriptions.Item>
            )}
            {stop.openEnded && (
              <Descriptions.Item label="Open Ended">
                <div style={{ fontStyle: 'italic', color: '#555', marginBottom: 4 }}>"{stop.openEnded.answer}"</div>
                {stop.openEnded.feedback && <div style={{ marginBottom: 4 }}>Feedback: {stop.openEnded.feedback}</div>}
                <Tag color={stop.openEnded.correct ? 'green' : 'red'}>
                  {stop.openEnded.correct ? 'Correct' : 'Incorrect'}
                </Tag>
              </Descriptions.Item>
            )}
            {stop.video && (
              <Descriptions.Item label="Video">
                <span style={{ marginRight: 16 }}>Status: {stop.video.processing ? 'Processing' : 'Complete'}</span>
                {stop.video.feedback && <div>Feedback: {stop.video.feedback}</div>}
              </Descriptions.Item>
            )}
            {!stop.mc && !stop.openEnded && !stop.video && (
              <Descriptions.Item label="Details">No specific interactions recorded.</Descriptions.Item>
            )}
          </Descriptions>
        </div>
      ))}
    </div>
  )
}

const LeaderboardTable = ({
  allUser,
  isHistorical = false,
  loading = false,
  leaderboardData = {},
  historicalData = [],
  selectedCompetition,
  userId,
  orgId,
  activeTab = 'team'
}) => {
  // Move ALL hooks to the top - before any conditional logic or returns
  const [activeFilter, setActiveFilter] = useState('All Time')
  const [searchTerm, setSearchTerm] = useState('')
  const [badgeModalVisible, setBadgeModalVisible] = useState(false)
  const [selectedBadges, setSelectedBadges] = useState([])
  const [tableLoading, setTableLoading] = useState(false)
  const [filteredTableData, setFilteredTableData] = useState([])

  // Pagination state
  const [page, setPage] = useState(1)
  const limit = 10
  const { t } = useTranslation()
  const filters = [
    t('reports:all_time'),
    t('reports:today'),
    t('v5:this_week'),
    t('reports:this_month'),
    t('v5:this_year')
  ]

  // Memoize the leaderboard data to prevent infinite loops
  const memoizedLeaderboardData = useMemo(() => {
    return leaderboardData || {}
  }, [leaderboardData])

  const memoizedHistoricalData = useMemo(() => {
    return historicalData || []
  }, [historicalData])

  useEffect(() => {
    const data =
      (isHistorical
        ? memoizedHistoricalData
        : memoizedLeaderboardData && memoizedLeaderboardData.leaderboard) || []

    // Only update if data actually changed
    setFilteredTableData(prevData => {
      if (JSON.stringify(prevData) !== JSON.stringify(data)) {
        return data
      }
      return prevData
    })
  }, [isHistorical, memoizedHistoricalData, memoizedLeaderboardData])

  // Reset page when search term changes
  useEffect(() => {
    setPage(1)
  }, [searchTerm])

  // Now you can safely return early if needed (for testing purposes)
  // Comment out or remove this line when you want the full component to render
  // return <div>LeaderboardTable</div>

  // Memoize functions to prevent unnecessary re-renders
  const getDateRangeForFilter = useCallback(filter => {
    const now = moment()
    switch (filter) {
      case 'Today':
        return {
          from: now.startOf('day').format('YYYY-MM-DD'),
          to: now.endOf('day').format('YYYY-MM-DD')
        }
      case 'This Week':
        return {
          from: now.startOf('week').format('YYYY-MM-DD'),
          to: now.endOf('week').format('YYYY-MM-DD')
        }
      case 'This Month':
        return {
          from: now.startOf('month').format('YYYY-MM-DD'),
          to: now.endOf('month').format('YYYY-MM-DD')
        }
      case 'This Year':
        return {
          from: now.startOf('year').format('YYYY-MM-DD'),
          to: now.endOf('year').format('YYYY-MM-DD')
        }
      case 'All Time':
      default:
        return {
          from: '',
          to: ''
        }
    }
  }, []) // Empty dependency array since this function doesn't depend on any props/state

  const fetchFilteredData = useCallback(
    async filter => {
      try {
        setTableLoading(true)
        const dateRange = getDateRangeForFilter(filter)

        let response

        if (activeTab === 'team') {
          // Team tab - requires selectedCompetition
          if (!selectedCompetition || !selectedCompetition.value || !userId) {
            setFilteredTableData([])
            return
          }

          response = await api.leaderboard.getCompetitionUser({
            id: selectedCompetition.value,
            userId: userId,
            date: '',
            from: dateRange.from,
            to: dateRange.to
          })

          setFilteredTableData(
            (response.data && response.data.leaderboard) || []
          )
        } else if (activeTab === 'global') {
          // Global tab - use global competitions API
          response = await api.leaderboard.getGlobalCompetitions({
            from: dateRange.from,
            to: dateRange.to
          })

          setFilteredTableData(
            (response.data && response.data.leaderboard) || []
          )
        } else if (activeTab === 'quest') {
          if (!selectedCompetition || !selectedCompetition.value) {
            setFilteredTableData([])
            return
          }

          response = await api.quest.getQuestLeaderboard({
            huntId: selectedCompetition.value,
            user_id: userId,
            org_id: orgId,
            from: dateRange.from,
            to: dateRange.to
          })

          setFilteredTableData(
            (response.data && response.data.leaderboard) || []
          )
        }
      } catch (error) {
        console.error('Error fetching filtered leaderboard data:', error)
        setFilteredTableData([])
      } finally {
        setTableLoading(false)
      }
    },
    [
      activeTab,
      selectedCompetition && selectedCompetition.value,
      userId,
      getDateRangeForFilter
    ]
  ) // Add dependencies

  const handleFilterChange = useCallback(
    async filter => {
      setActiveFilter(filter)
      setPage(1)
      if (!isHistorical) {
        await fetchFilteredData(filter)
      }
    },
    [isHistorical, fetchFilteredData]
  )

  // Memoize table data to prevent unnecessary recalculations
  const tableData = useMemo(() => {
    return filteredTableData.map((item, index) => {
      if (activeTab === 'quest') {
        return {
          key: item.groupId || `quest-${index}`,
          rank: item.rank || index + 1,
          groupName: item.groupName || 'Unknown Group',
          points: item.points || 0,
          completedStops: item.completedStops || 0,
          completed: item.completed || false,
          groupId: item.groupId
        }
      }

      const user = item && item.user
      const userName =
        user &&
        (user.first_name && user.last_name
          ? `${user.first_name} ${user.last_name}`
          : user.username || user.email)
      const userAvatar = (user && user.avatar && user.avatar.link) || ''

      // Prefer period-specific points when a date filter is applied
      const pointsForRange =
        typeof item?.points_in_range === 'number'
          ? item.points_in_range
          : typeof item?.points === 'number'
            ? item.points
            : null
      const totalPointsValue =
        activeFilter === 'All Time'
          ? typeof item?.total_points === 'number'
            ? item.total_points
            : pointsForRange || 0
          : pointsForRange != null
            ? pointsForRange
            : typeof item?.total_points === 'number'
              ? item.total_points
              : 0

      return {
        key: item.rank || index,
        rank: item.rank,
        user: userName || 'Unknown User',
        avatar: userAvatar || '👤',
        totalPoints: totalPointsValue,
        level: item.level || 'N/A',
        badges: item.badges || [],
        progress: item.progress || 0
      }
    })
  }, [filteredTableData, activeFilter, activeTab])

  const filteredData = useMemo(() => {
    return tableData.filter(user => {
      if (activeTab === 'quest') {
        return user.groupName.toLowerCase().includes(searchTerm.toLowerCase())
      }
      return user.user.toLowerCase().includes(searchTerm.toLowerCase())
    })
  }, [tableData, searchTerm, activeTab])

  // Paginated data
  const paginatedData = useMemo(() => {
    const startIndex = (page - 1) * limit
    const endIndex = startIndex + limit
    return filteredData.slice(startIndex, endIndex)
  }, [filteredData, page, limit])

  // Pagination configuration
  const paginationConfig = useMemo(
    () => ({
      current: page,
      pageSize: limit,
      total: filteredData.length,
      showSizeChanger: false,
      showQuickJumper: false,
      showTotal: (total, range) =>
        `${range[0]}-${range[1]} ${t('general:of')} ${total} ${t('v3:items')}`,
      onChange: newPage => setPage(newPage)
    }),
    [page, limit, filteredData.length]
  )

  const getRankSuffix = useCallback(rank => {
    if (rank <= 3) {
      return rank === 1 ? 'st' : rank === 2 ? 'nd' : 'rd'
    }
    return 'th'
  }, [])

  const gotoHistory = useCallback(() => {
    history.push('/leaderboard/history')
  }, [])

  const handleBadgeClick = useCallback(badges => {
    setSelectedBadges(badges)
    setBadgeModalVisible(true)
  }, [])

  const handleModalClose = useCallback(() => {
    setBadgeModalVisible(false)
    setSelectedBadges([])
  }, [])

  const handleExportClick = useCallback(() => {
    if (isHistorical) return

    // Prepare CSV header
    const header = ['Rank', 'User', 'Total Points', 'Level', 'Number of Badges']

    // Prepare CSV data rows
    const rows = tableData.map(row => [
      row.rank,
      row.user,
      row.totalPoints,
      row.level,
      row.badges && row.badges.length > 0 ? row.badges.length : 0
    ])

    // Combine header and rows
    const csvContent = [header, ...rows]
      .map(e => e.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n')

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'leaderboard.csv'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, [isHistorical, tableData])

  // Memoize columns to prevent unnecessary re-renders
  const columns = useMemo(() => {
    if (activeTab === 'quest') {
      return [
        {
          title: t('v4:rank') || 'Rank',
          dataIndex: 'rank',
          key: 'rank',
          width: 100,
          render: rank => (
            <div className="rank-cell">
              <span className="rank-number">{rank}</span>
              <span className="rank-suffix">{getRankSuffix(rank)}</span>
            </div>
          )
        },
        {
          title: 'Group Name',
          dataIndex: 'groupName',
          key: 'groupName',
          width: 250,
          render: name => <span className="user-name" style={{ fontWeight: '600' }}>{name}</span>
        },
        {
          title: 'Points',
          dataIndex: 'points',
          key: 'points',
          width: 120,
          render: points => <span className="points-cell">{points}</span>
        },
        {
          title: 'Completed Stops',
          dataIndex: 'completedStops',
          key: 'completedStops',
          width: 150
        },
        {
          title: 'Status',
          dataIndex: 'completed',
          key: 'completed',
          width: 120,
          render: completed => (
            <span style={{ color: completed ? '#52c41a' : '#faad14', fontWeight: '500' }}>
              {completed ? 'Completed' : 'In Progress'}
            </span>
          )
        }
      ]
    }

    return [
      {
        title: t('v4:rank'),
        dataIndex: 'rank',
        key: 'rank',
        width: 100,
        render: rank => (
          <div className="rank-cell">
            <span className="rank-number">{rank}</span>
            <span className="rank-suffix">{getRankSuffix(rank)}</span>
          </div>
        )
      },
      {
        title: t('reports:user'),
        dataIndex: 'user',
        key: 'user',
        width: 200,
        render: (user, record) => (
          <div className="user-info">
            <Avatar className="user-avatar" src={record.avatar} />
            <span className="user-name">{user}</span>
          </div>
        )
      },
      {
        title: t('v2:total_points'),
        dataIndex: 'totalPoints',
        key: 'totalPoints',
        width: 120,
        render: points => <span className="points-cell">{points}</span>
      },
      {
        title: t('v4:level'),
        dataIndex: 'level',
        key: 'level',
        width: 100,
        render: level => <span className="level-cell">{level}</span>
      },
      {
        title: t('v2:badges'),
        dataIndex: 'badges',
        key: 'badges',
        width: 120,
        render: badges => (
          <div
            className="badges-container"
            onClick={() => badges.length > 0 && handleBadgeClick(badges)}
            style={{ cursor: badges.length > 0 ? 'pointer' : 'default' }}
          >
            <div className="badges-stack">
              {badges.slice(0, 3).map((badge, index) => (
                <img
                  key={index}
                  src={badge.link}
                  alt={`Badge ${index + 1}`}
                  className="badge-image"
                />
              ))}
            </div>
            {badges.length > 3 && (
              <span className="badges-count">+{badges.length - 3}</span>
            )}
          </div>
        )
      },
      {
        title: t('v4:progress'),
        dataIndex: 'progress',
        key: 'progress',
        width: 150,
        render: progress => (
          <div className="table-progress">
            <div className="table-progress-bar">
              <div
                className="table-progress-fill"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <span className="table-progress-text">{progress}%</span>
          </div>
        )
      }
    ]
  }, [activeTab, getRankSuffix, handleBadgeClick, t])

  return (
    <div className="leaderboard-table-section">
      {!isHistorical && (
        <Fragment>
          <h3 className="leaderboard-table-title">{t('v4:leaderboard')}</h3>
          <div className="leaderboard-controls">
            <div className="filter-tabs">
              {filters.map(filter => (
                <button
                  key={filter}
                  className={`filter-tab ${activeFilter === filter ? 'active' : ''
                    }`}
                  onClick={() => handleFilterChange(filter)}
                >
                  {filter}
                </button>
              ))}
            </div>
            <div className="search-controls">
              <Button className="export-btn" onClick={handleExportClick}>
                <Icon type="download" /> {t('v4:export')}
              </Button>
              <Button className="history-btn" onClick={gotoHistory}>
                <Icon type="reload" /> {t('v4:history')}
              </Button>
              <Input.Search
                allowClear
                placeholder={t('v4:search_by_name')}
                className="leaderboard-search-input"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </Fragment>
      )}
      {isHistorical && (
        <div className="leaderboard-controls-history">
          <h3 className="leaderboard-table-title">
            {t('v4:historical_leaderboard')}
          </h3>
          <div className="leaderboard-history-controls">
            <Input.Search
              placeholder={t('v4:search_by_name')}
              className="leaderboard-search-input"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              size="small"
            />
          </div>
        </div>
      )}
      <div className="table-container">
        <Table
          loading={loading || tableLoading}
          columns={columns}
          dataSource={paginatedData}
          pagination={paginationConfig}
          locale={{
            emptyText: (
              <div className="no-data-message">
                {isHistorical
                  ? t('v4:no_historical_data_available_for_the_selected_period')
                  : t('v4:no_leaderboard_data_available')}
              </div>
            )
          }}
          className="leaderboard-ant-table"
          expandedRowRender={activeTab === 'quest' ? record => <ExpandedQuestRow groupId={record.groupId} /> : undefined}
          expandRowByClick={activeTab === 'quest'}
        />
      </div>

      {/* Badge Modal */}
      <Modal
        title="User Badges"
        visible={badgeModalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={600}
        centered
        className="badge-modal"
      >
        <div className="badge-grid">
          {selectedBadges.map((badge, index) => (
            <div key={index} className="badge-grid-item">
              <ImagePreview
                src={badge.link}
                alt={`Badge ${index + 1}`}
                className="badge-grid-image"
              />
            </div>
          ))}
        </div>
      </Modal>
    </div>
  )
}

export default LeaderboardTable
